/**
 * SP model viewer tab - a single-page service-power explorer:
 *
 *   ┌ CONTROLS ──────────────────────────────────────────────────┐
 *   │ version · init-datetime · Load values                       │
 *   ├ EXPLORER ──────────────┬ DEPENDENCE PLOT ────────────────────┤
 *   │ held-constant inputs   │ "SP vs ▼feature": predicted SP swept │
 *   │ (sliders + histograms, │ over the chosen feature (others held │
 *   │ stacked) + hour control │ at the sliders) as a line, with the │
 *   │ + locked derived chips  │ actual data overlaid as a scatter    │
 *   ├ TIMELINE ──────────────┴─────────────────────────────────────┤
 *   │ corrected vs actual over the window - click to load a moment │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * The ship is the first of the Model Viewer's picked ships (`modelShips`); the
 * distribution / timeline window is `modelStart`/`modelEnd` - the sidebar's
 * Query card, shared with every model and separate from Explore's. The user perturbs base inputs
 * with sliders and a single hour control; the derived features
 * (`enthalpy`/`hour_sin`/`hour_cos`) are LOCKED - recomputed from the base inputs
 * (see ./derived.ts) so the vector is always self-consistent.
 *
 * The right panel plots SP's dependence on ONE feature at a time: that feature is
 * swept across its slider range (and dropped from the left controls) while every
 * other input stays at its slider value; the resulting curve is drawn as a line,
 * and real rows near the slider settings are overlaid as a scatter (see
 * runSweep + renderDependencePlot). The SHAP decomposition is hidden for now but
 * the code is retained (see the "SHAP contributions" block).
 *
 * Service power is reported CORRECTED: predicted + the additive SP correction
 * factor in force at the loaded timestamp (read from DynamoDB by the backend).
 * The factor depends only on ship + time, so it's fetched once per load and held
 * constant while sliders move.
 *
 * Histograms, the dependence plot and the timeline are drawn as small inline SVGs
 * rather than uPlot instances - lighter for this many marks and trivial to
 * overlay markers / wire up click-to-load on.
 */

import { html, svg, nothing, type SVGTemplateResult, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeTab,
  activeModelTab,
  modelShips,
  modelStart,
  modelEnd,
  spVersions,
  spVersion,
  spSchema,
  spDistributions,
  spValues,
  spActual,
  spPredicted,
  spCorrection,
  spCorrectionTimestamp,
  spInitDatetime,
  spLoading,
  spError,
  spHour,
  spExplain,
  spTimeseries,
  spTimeseriesLoading,
  spCorrectionsSeries,
  spXFeature,
  spSweep,
  spSweepLoading,
} from '../../state/store.ts';
import {
  getSpVersions,
  getSpSchema,
  runSpDistributions,
  spInit,
  spPredict,
  spExplain as runSpExplain,
  spTimeseries as runSpTimeseries,
  spCorrections as runSpCorrections,
  runSpSweep,
} from '../../api/endpoints.ts';
import type {
  SPContribution,
  SPCorrectionPoint,
  SPDistFeature,
  SPFeature,
} from '../../api/types.ts';
import {
  DERIVED_FEATURES,
  computeEnthalpy,
  hourToCyclical,
  cyclicalToHour,
} from './derived.ts';
import { userMessage } from '../../api/errors.ts';

const PREDICT_DEBOUNCE_MS = 250;
const DERIVED_SET = new Set<string>(DERIVED_FEATURES);

/** Dependence-plot line resolution (model evaluations across the swept range). */
const SWEEP_POINTS = 60;
/** Half-width of the scatter's conditioning window, as a fraction of each
 * (non-swept) feature's slider span: a real row joins the cloud only when every
 * other input sits within ±TOL_FRAC·(hi−lo) of its current slider value. Keeps
 * the scatter comparable to the "all others held constant" line while still
 * admitting enough nearby rows to be useful. Conditioning on several features at
 * once thins the cloud fast - ±15% keeps a usable number of points where a
 * tighter window (e.g. ±10%) can collapse to a handful near the distribution
 * modes. */
const TOL_FRAC = 0.15;

/** The model is only valid above this speed (knots) - eos-online-models and the
 * corrector both filter `stw > 5` before features. Mirrors STW_MIN_TRAINING in
 * backend/sp_model.py. */
const STW_MIN = 5;

/** The cyclical hour pair is merged into one "hour of day" contribution. */
const HOUR_PAIR = new Set(['hour_sin', 'hour_cos']);
const HOUR_LABEL = 'Hour of day';
/** Pseudo x-feature for the dependence plot: sweep hour-of-day (0–23) rather than
 * a single model column - it drives the cyclical hour_sin/hour_cos pair. Mirrors
 * SWEEP_HOUR_FEATURE in backend/sp_model.py. */
const HOUR_X = 'hour';

/** Display units per feature (shown next to values and slider bounds). */
const UNITS: Record<string, string> = {
  air_temp: '°C',
  sea_water_temp: '°C',
  humidity: '%',
  air_pressure: 'hPa',
  stw: 'kn',
  sog: 'kn',
  latitude: '°',
  longitude: '°',
  enthalpy: 'kJ/kg',
};
const unitOf = (name: string): string => UNITS[name] ?? '';

/** Human-readable display labels per model feature (the raw canonical name is
 * kept in `title=` attributes for reference). Unmapped names fall back to the
 * raw identifier. */
const FEATURE_LABELS: Record<string, string> = {
  air_temp: 'Air temperature',
  sea_water_temp: 'Sea water temperature',
  humidity: 'Humidity',
  air_pressure: 'Air pressure',
  stw: 'Speed through water',
  sog: 'Speed over ground',
  latitude: 'Latitude',
  longitude: 'Longitude',
  enthalpy: 'Enthalpy',
};
const labelOf = (name: string): string => FEATURE_LABELS[name] ?? name;

/** "Nice" axis bounds + tick values (Heckbert): rounds [lo, hi] out to a
 * 1/2/5×10ⁿ step so the y-axis grid lands on readable round numbers
 * (…, 5000, 10000, …) instead of arbitrary data-derived values. */
function niceTicks(
  lo: number,
  hi: number,
  target = 5,
): { min: number; max: number; ticks: number[] } {
  const niceNum = (range: number, round: boolean): number => {
    const exp = Math.floor(Math.log10(range));
    const f = range / 10 ** exp; // 1..10
    let nf: number;
    if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
    else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nf * 10 ** exp;
  };
  const range = niceNum(hi - lo || 1, false);
  const step = niceNum(range / Math.max(1, target - 1), true);
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;
  const count = Math.min(20, Math.round((max - min) / step));
  const ticks: number[] = [];
  for (let i = 0; i <= count; i++) ticks.push(min + i * step);
  return { min, max, ticks };
}

/** Center of the tallest histogram bin - a sensible default feature value. */
function modeValue(d: SPDistFeature): number {
  if (!d.counts.length || d.edges.length < 2) return d.min ?? 0;
  let bestIdx = 0;
  let best = -1;
  d.counts.forEach((c, i) => {
    if (c > best) {
      best = c;
      bestIdx = i;
    }
  });
  return (d.edges[bestIdx]! + d.edges[bestIdx + 1]!) / 2;
}

/** General-purpose number format for feature values (not kW). */
function fmt(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return '-';
  const abs = Math.abs(v);
  if (abs < 1e-9) return '0'; // floating-point zero (e.g. sin(π)) reads as 0, not 1e-16
  if (abs >= 1e5 || abs < 1e-3) return v.toExponential(2);
  return v.toLocaleString(undefined, { maximumFractionDigits: digits });
}

/** A feature value with its unit appended (e.g. "21.4 °C"). */
function fmtVal(name: string, v: number | null | undefined): string {
  const u = unitOf(name);
  return u ? `${fmt(v)} ${u}` : fmt(v);
}

/** Plain value for the editable field: capped at 2 decimals and WITHOUT thousands
 * grouping, so it round-trips through `Number()` (unlike `fmt`, which localises
 * "1013.2" to "1,013.2" and would fail to parse). */
function fmtPlain(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '';
  return String(Number(v.toFixed(2)));
}

/** Integer kW with a thin-space (U+202F) thousands separator and NO decimal
 * point as separator (e.g. 12 345). `signed` prefixes a leading + for positives. */
function fmtKw(v: number | null | undefined, signed = false): string {
  if (v == null || !Number.isFinite(v)) return '-';
  const rounded = Math.round(v);
  const sign = rounded < 0 ? '-' : signed ? '+' : '';
  const digits = String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${sign}${digits}`;
}

/** ISO timestamp → compact "MM-DD HH:MM" for the hover readout. */
function fmtTs(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/** ISO timestamp → full "YYYY-MM-DD HH:MM" (UTC) for the timeline x-axis ticks. */
function fmtTsFull(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

@customElement('spmodel-viewer')
export class SpModelViewer extends LightElement {
  private store = new StoreController(this, [
    activeTab,
    activeModelTab,
    modelShips,
    modelStart,
    modelEnd,
    spVersions,
    spVersion,
    spSchema,
    spDistributions,
    spValues,
    spActual,
    spPredicted,
    spCorrection,
    spCorrectionTimestamp,
    spInitDatetime,
    spLoading,
    spError,
    spHour,
    spExplain,
    spTimeseries,
    spTimeseriesLoading,
    spCorrectionsSeries,
    spXFeature,
    spSweep,
    spSweepLoading,
  ]);

  @state() private initing = false;
  /** Hovered timeline sample index (for the cursor + readout), or null. */
  @state() private hoverIdx: number | null = null;
  /** Hovered dependence-plot grid index (for the cursor + readout), or null. */
  @state() private sweepHoverIdx: number | null = null;
  /** Whether the SHAP explainer popover is pinned open (click toggle). */
  @state() private shapInfoOpen = false;
  /** Whether the datetime/load explainer popover is pinned open. */
  @state() private dateInfoOpen = false;
  /** Whether the "what does actual mean" (ALS/AAQS) popover is pinned open. */
  @state() private actualInfoOpen = false;

  // Load-guard keys so reactive updates don't refire in-flight fetches.
  private versionsForShip: string | null = null;
  private distKey = '';
  private tsKey = '';
  private predictTimer: ReturnType<typeof setTimeout> | null = null;
  private explainTimer: ReturnType<typeof setTimeout> | null = null;
  private sweepTimer: ReturnType<typeof setTimeout> | null = null;
  // Guards re-entrant /sweep fetches: only the latest request's result is applied.
  private sweepSeq = 0;

  override updated(): void {
    void this.ensureLoaded();
  }

  /** Fetch versions, then schema + distributions + timeseries, as inputs change. */
  private async ensureLoaded(): Promise<void> {
    if (activeTab.value !== 'modelviewer' || activeModelTab.value !== 'sp') return;
    const ship = modelShips.value[0] ?? null;
    if (!ship) return;

    if (this.versionsForShip !== ship) {
      this.versionsForShip = ship;
      // Invalidate the previous ship's version + schema SYNCHRONOUSLY before the
      // async version fetch. updated() fires ensureLoaded() on every render
      // without awaiting, so a concurrent call would otherwise read the old
      // ship's spVersion and fire loadDistributions against a version that may
      // not exist for the new ship (a 404). Clearing it first makes those
      // concurrent calls bail at the `!version` guard below; loadVersions then
      // picks this ship's latest.
      spVersion.set(null);
      spSchema.set(null);
      this.distKey = '';
      this.tsKey = '';
      await this.loadVersions(ship);
      return; // setting the version re-triggers updated() → distributions
    }

    const version = spVersion.value;
    if (!version) return;
    const key = `${ship}|${version}|${modelStart.value}|${modelEnd.value}`;
    if (this.distKey !== key) {
      this.distKey = key;
      await this.loadDistributions(ship, version);
    }
    if (this.tsKey !== key) {
      this.tsKey = key;
      await this.loadTimeseries(ship, version);
    }
  }

  private async loadVersions(ship: string): Promise<void> {
    spError.set(null);
    spLoading.set(true);
    try {
      const res = await getSpVersions(ship);
      spVersions.set(res.versions);
      if (!res.versions.length) {
        spVersion.set(null);
        spSchema.set(null);
        spDistributions.set([]);
        spTimeseries.set(null);
        spExplain.set(null);
        spError.set(`No service-power model is available for ${ship}.`);
        this.distKey = '';
        this.tsKey = '';
        return;
      }
      if (!spVersion.value || !res.versions.includes(spVersion.value)) {
        spVersion.set(res.latest ?? res.versions[0]!);
      }
    } catch (e) {
      // Keep `versionsForShip` set so a failure doesn't re-trigger the fetch on
      // the next render - that would loop and repeatedly surface an error.
      // A retry happens naturally when the ship changes.
      spError.set(userMessage(e, 'Failed to load model versions.'));
    } finally {
      spLoading.set(false);
    }
  }

  private async loadDistributions(ship: string, version: string): Promise<void> {
    spError.set(null);
    spLoading.set(true);
    spActual.set(null);
    spCorrection.set(null);
    spCorrectionTimestamp.set(null);
    try {
      const [schema, dist] = await Promise.all([
        getSpSchema(ship, version),
        runSpDistributions({
          ship_id: ship,
          version,
          start_date: modelStart.value,
          end_date: modelEnd.value,
        }),
      ]);
      spSchema.set(schema);
      spDistributions.set(dist.features);

      // Seed the feature vector at each feature's distribution mode so the
      // sliders have sensible positions immediately, then lock the derived
      // features to values consistent with the seeded base inputs.
      const values: Record<string, number> = {};
      for (const f of dist.features) {
        if (f.kind !== 'missing' && f.n > 0) values[f.name] = modeValue(f);
      }
      spHour.set(12);
      spValues.set(this.recomputeDerived(values, schema.features));

      // Default the dependence plot's x-axis to hour-of-day (the most telling
      // dependence) when the model has it, else a direct feature (prefer STW),
      // keeping any still-valid prior choice.
      const directNames = schema.features
        .filter((f) => f.kind === 'direct')
        .map((f) => f.name);
      const sweepable = [...(this.modelHasHour ? [HOUR_X] : []), ...directNames];
      if (!spXFeature.value || !sweepable.includes(spXFeature.value)) {
        spXFeature.set(sweepable[0] ?? null);
      }
      this.runSweep();

      // Then overwrite from the latest available real row (also fetches the
      // correction factor + syncs the datetime picker). Silent: if it fails the
      // mode-seed above stays in place.
      await this.loadAt(null, true);
    } catch (e) {
      // Keep `distKey` set (don't reset) so the failed load isn't re-issued on
      // every render, which would loop. Retries when ship/version/range change.
      spError.set(userMessage(e, 'Failed to load distributions.'));
      spSchema.set(null);
      spDistributions.set([]);
    } finally {
      spLoading.set(false);
    }
  }

  private async loadTimeseries(ship: string, version: string): Promise<void> {
    spTimeseriesLoading.set(true);
    this.hoverIdx = null;
    // The correction strip shares the timeline window; fetch it alongside (best
    // effort - a corrections outage just drops the strip, never the timeline).
    void runSpCorrections({
      ship_id: ship,
      start_date: modelStart.value,
      end_date: modelEnd.value,
    })
      .then((c) => spCorrectionsSeries.set(c))
      .catch(() => spCorrectionsSeries.set(null));
    try {
      const ts = await runSpTimeseries({
        ship_id: ship,
        version,
        start_date: modelStart.value,
        end_date: modelEnd.value,
      });
      spTimeseries.set(ts);
    } catch {
      // Keep `tsKey` set so this doesn't loop; the timeline just shows empty.
      spTimeseries.set(null);
    } finally {
      spTimeseriesLoading.set(false);
    }
  }

  /** Per-feature kind lookup from the loaded schema. */
  private kindOf(name: string): SPFeature['kind'] | undefined {
    return spSchema.value?.features.find((f) => f.name === name)?.kind;
  }

  /** Overwrite the locked derived features from the base inputs + hour control,
   * leaving them untouched where they aren't derivable for this ship. */
  private recomputeDerived(
    values: Record<string, number>,
    features: SPFeature[] = spSchema.value?.features ?? [],
  ): Record<string, number> {
    const out = { ...values };
    const kind = (n: string) => features.find((f) => f.name === n)?.kind;
    if (kind('enthalpy') === 'derived') {
      const e = computeEnthalpy(out.air_temp!, out.humidity!, out.air_pressure!);
      if (Number.isFinite(e)) out.enthalpy = e;
    }
    const { hour_sin, hour_cos } = hourToCyclical(spHour.value);
    if (kind('hour_sin') === 'derived') out.hour_sin = hour_sin;
    if (kind('hour_cos') === 'derived') out.hour_cos = hour_cos;
    return out;
  }

  /** True when the model can't run because some feature isn't in this ship's data. */
  private get hasMissing(): boolean {
    return (spSchema.value?.features ?? []).some((f) => f.kind === 'missing');
  }

  /** True when the model has a (resolvable) hour-of-day feature - i.e. the hour
   * control is shown and "Hour of day" can be swept on the dependence plot. */
  private get modelHasHour(): boolean {
    return (spSchema.value?.features ?? []).some(
      (f) => HOUR_PAIR.has(f.name) && f.kind === 'derived',
    );
  }

  private runPredict(): void {
    if (this.predictTimer) clearTimeout(this.predictTimer);
    const ship = modelShips.value[0] ?? null;
    const version = spVersion.value;
    if (!ship || !version || this.hasMissing) {
      spPredicted.set(null);
      return;
    }
    const features = { ...spValues.value };
    this.predictTimer = setTimeout(async () => {
      try {
        const res = await spPredict({ ship_id: ship, version, features });
        spPredicted.set(res.predicted);
      } catch {
        spPredicted.set(null);
      }
    }, PREDICT_DEBOUNCE_MS);
  }

  private runExplain(): void {
    if (this.explainTimer) clearTimeout(this.explainTimer);
    const ship = modelShips.value[0] ?? null;
    const version = spVersion.value;
    if (!ship || !version || this.hasMissing) {
      spExplain.set(null);
      return;
    }
    const features = { ...spValues.value };
    this.explainTimer = setTimeout(async () => {
      try {
        spExplain.set(await runSpExplain({ ship_id: ship, version, features }));
      } catch {
        spExplain.set(null);
      }
    }, PREDICT_DEBOUNCE_MS);
  }

  /** Recompute the dependence sweep for the current x-feature + slider settings,
   * debounced. Holds every other input at its slider value, varies the x-feature
   * across its slider range, and conditions the actual-data scatter on the other
   * sliders (± TOL_FRAC of each span - see TOL_FRAC). A monotonically increasing
   * sequence guards against an earlier (slower) request overwriting a later one. */
  private runSweep(): void {
    if (this.sweepTimer) clearTimeout(this.sweepTimer);
    const ship = modelShips.value[0] ?? null;
    const version = spVersion.value;
    const xFeature = spXFeature.value;
    if (!ship || !version || this.hasMissing || !xFeature) {
      spSweep.set(null);
      spSweepLoading.set(false);
      return;
    }
    const dists = spDistributions.value;
    const isHour = xFeature === HOUR_X;
    // X range: hour-of-day is 0–23; a feature uses its slider span (= histogram caps).
    let xMin: number;
    let xMax: number;
    let xPoints: number;
    if (isHour) {
      xMin = 0;
      xMax = 23;
      xPoints = 24;
    } else {
      const xDist = dists.find((d) => d.name === xFeature);
      if (!xDist || xDist.min == null || xDist.max == null || xDist.max === xDist.min) {
        spSweep.set(null);
        spSweepLoading.set(false);
        return;
      }
      xMin = xDist.min;
      xMax = xDist.max;
      xPoints = SWEEP_POINTS;
    }
    const features = { ...spValues.value };
    // Condition the scatter on every OTHER direct feature: a ± window sized to a
    // fraction of that feature's slider span. (An hour sweep excludes none, since
    // the swept "hour" isn't itself a direct slider feature.)
    const tolerances: Record<string, number> = {};
    for (const d of dists) {
      if (d.name === xFeature || d.kind !== 'direct') continue;
      if (d.min == null || d.max == null || d.max <= d.min) continue;
      tolerances[d.name] = TOL_FRAC * (d.max - d.min);
    }
    const seq = ++this.sweepSeq;
    spSweepLoading.set(true);
    this.sweepTimer = setTimeout(async () => {
      try {
        const res = await runSpSweep({
          ship_id: ship,
          version,
          x_feature: xFeature,
          features,
          x_min: xMin,
          x_max: xMax,
          points: xPoints,
          tolerances,
          start_date: modelStart.value,
          end_date: modelEnd.value,
        });
        if (seq !== this.sweepSeq) return; // a newer request superseded this one
        spSweep.set(res);
      } catch {
        if (seq === this.sweepSeq) spSweep.set(null);
      } finally {
        if (seq === this.sweepSeq) spSweepLoading.set(false);
      }
    }, PREDICT_DEBOUNCE_MS);
  }

  private onSlider(name: string, e: Event): void {
    const v = Number((e.target as HTMLInputElement).value);
    if (!Number.isFinite(v)) return;
    const next = this.recomputeDerived({ ...spValues.value, [name]: v });
    spValues.set(next);
    spActual.set(null); // perturbed away from the loaded snapshot
    this.runSweep();
  }

  /** Commit an exact value typed into a feature's value field (Enter or blur).
   * Clamps to the slider domain so the thumb + histogram stay consistent, and
   * reflects the normalised value back into the field (rejecting non-numbers). */
  private onValueEdit(name: string, lo: number, hi: number, e: Event): void {
    const input = e.target as HTMLInputElement;
    const raw = Number(input.value.trim());
    const cur = spValues.value[name];
    if (!Number.isFinite(raw)) {
      input.value = fmtPlain(cur); // reject: restore the current value
      return;
    }
    const v = Math.min(hi, Math.max(lo, raw));
    input.value = fmtPlain(v); // reflect clamping / normalisation
    if (v === cur) return;
    spValues.set(this.recomputeDerived({ ...spValues.value, [name]: v }));
    spActual.set(null); // perturbed away from the loaded snapshot
    this.runSweep();
  }

  private onHour(e: Event): void {
    const h = Number((e.target as HTMLInputElement).value);
    if (!Number.isFinite(h)) return;
    spHour.set(h);
    spValues.set(this.recomputeDerived({ ...spValues.value }));
    spActual.set(null);
    this.runSweep();
  }

  /** Switch the feature swept on the dependence plot's x-axis. */
  private onXFeature(e: Event): void {
    const name = (e.target as HTMLSelectElement).value;
    if (!name || name === spXFeature.value) return;
    spXFeature.set(name);
    this.sweepHoverIdx = null;
    // Drop the previous feature's curve so it isn't shown under the new axis
    // (different units/range) while the new sweep is in flight.
    spSweep.set(null);
    this.runSweep();
  }

  private onInitDatetime(e: Event): void {
    spInitDatetime.set((e.target as HTMLInputElement).value);
  }

  /** Load the real feature vector + correction factor at a datetime (or the
   * latest available row when `dt` is null) into the explorer. */
  private async loadAt(dt: string | null, silent = false): Promise<void> {
    const ship = modelShips.value[0] ?? null;
    const version = spVersion.value;
    if (!ship || !version) return;
    this.initing = true;
    if (!silent) spError.set(null);
    try {
      const res = await spInit({ ship_id: ship, version, datetime: dt });
      const merged = { ...spValues.value };
      for (const [k, v] of Object.entries(res.values)) {
        if (v != null && Number.isFinite(v)) merged[k] = v;
      }
      // Recover the hour control from the loaded cyclical pair so the slider
      // reflects the snapshot; keep the backend's exact derived values (so the
      // shown prediction matches) until the user next perturbs an input.
      if (
        merged.hour_sin != null &&
        merged.hour_cos != null &&
        this.kindOf('hour_sin') === 'derived'
      ) {
        spHour.set(cyclicalToHour(merged.hour_sin, merged.hour_cos));
      }
      spValues.set(merged);
      spActual.set(res.actual_service_power);
      spPredicted.set(res.predicted);
      spCorrection.set(res.correction);
      spCorrectionTimestamp.set(res.correction_timestamp);
      // Sync the picker to the actual row used (also seeds it on first load).
      // `res.timestamp` is a naive UTC string (e.g. "2026-06-13 22:02:00"); parse
      // it AS UTC - never let `new Date` read it as local, or each load would shift
      // the picker by the local offset and drift on every click.
      const raw = res.timestamp.replace(' ', 'T');
      const epoch = Date.parse(/[Zz]|[+-]\d\d:?\d\d$/.test(raw) ? raw : `${raw}Z`);
      if (Number.isFinite(epoch)) {
        spInitDatetime.set(new Date(epoch).toISOString().slice(0, 19));
      }
      this.runSweep();
    } catch (e) {
      if (!silent) {
        spError.set(userMessage(e, 'Failed to load data for that datetime.'));
      }
    } finally {
      this.initing = false;
    }
  }

  /** "Load values" button - load from the datetime currently in the picker. */
  private loadValues(): void {
    void this.loadAt(spInitDatetime.value || null);
  }

  /** "Load latest" button - load the most recent available row (and the
   * correction factor in force then), syncing the datetime picker to it. */
  private loadLatest(): void {
    void this.loadAt(null);
  }

  // ── Rendering ────────────────────────────────────────────

  private renderHistogram(
    d: SPDistFeature,
    value: number | undefined,
  ): TemplateResult {
    const W = 100;
    const H = 36;
    if (!d.counts.length || d.min == null || d.max == null || d.max === d.min) {
      return html`<div class="sp-hist-empty">no distribution</div>`;
    }
    const maxCount = Math.max(...d.counts, 1);
    const bw = W / d.counts.length;
    const bars = d.counts.map((c, i) => {
      const h = (c / maxCount) * H;
      return svgRect(i * bw, H - h, Math.max(bw - 0.5, 0.5), h);
    });
    const span = d.max - d.min;
    const markerX =
      value != null && Number.isFinite(value)
        ? Math.min(W, Math.max(0, ((value - d.min) / span) * W))
        : null;
    return html`
      <svg
        class="sp-hist"
        viewBox="0 0 ${W} ${H}"
        preserveAspectRatio="none"
        role="img"
        aria-label="${d.name} distribution"
      >
        <g class="sp-bars">${bars}</g>
        ${markerX == null
          ? nothing
          : svg`<line class="sp-marker" x1=${markerX} x2=${markerX} y1="0" y2=${H} />`}
      </svg>
    `;
  }

  /** A base (direct) feature: histogram + slider, or a muted "missing" card. */
  private renderFeature(f: SPFeature): TemplateResult {
    const dist = spDistributions.value.find((d) => d.name === f.name);
    const value = spValues.value[f.name];
    if (f.kind === 'missing' || !dist || dist.n === 0) {
      return html`
        <div class="sp-feature sp-feature-missing">
          <div class="sp-feature-head">
            <span class="sp-feature-name" title=${f.name}>${labelOf(f.name)}</span>
            <span class="sp-badge sp-badge-missing">unavailable</span>
          </div>
          <div class="sp-hist-empty">
            ${f.kind === 'missing' ? "not in this ship's data" : 'no data in selected window'}
          </div>
        </div>
      `;
    }
    const isStw = f.name === 'stw';
    // Slider spans the full distribution domain so its thumb stays vertically
    // aligned with the histogram marker above it. STW isn't clamped to the
    // training floor - the user can explore below it (the note flags the caveat).
    const lo = dist.min!;
    const hi = dist.max!;
    const step = (hi - lo) / 200 || 0.001;
    const unit = unitOf(f.name);
    // Size the value field to its content so wide numbers like "1013.86" aren't
    // clipped (a fixed width cut the digits and unit off the card's right edge).
    const shown = fmtPlain(value);
    return html`
      <div class="sp-feature">
        <div class="sp-feature-head">
          <span class="sp-feature-name" title=${f.name}>${labelOf(f.name)}</span>
          <span class="sp-feature-val">
            <input
              class="sp-val-input"
              style="width:${(Math.max(5, shown.length) + 2.5).toFixed(1)}ch"
              type="text"
              inputmode="decimal"
              aria-label="${labelOf(f.name)} value"
              .value=${shown}
              @focus=${(e: Event) => (e.target as HTMLInputElement).select()}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              @change=${(e: Event) => this.onValueEdit(f.name, lo, hi, e)}
            />
            ${unit ? html`<span class="sp-val-unit">${unit}</span>` : nothing}
          </span>
        </div>
        ${this.renderHistogram(dist, value)}
        <input
          class="sp-slider"
          type="range"
          min=${lo}
          max=${hi}
          step=${step}
          .value=${String(value ?? lo)}
          @input=${(e: Event) => this.onSlider(f.name, e)}
        />
        <div class="sp-range-labels">
          <span>${fmt(lo)}${unit ? ` ${unit}` : ''}</span>
          <span>${fmt(hi)}${unit ? ` ${unit}` : ''}</span>
        </div>
        ${isStw && value != null && value < STW_MIN
          ? html`<div class="sp-feature-note">
              ⚠︎ Below ${STW_MIN} kn - outside the model's trained range.
            </div>`
          : nothing}
      </div>
    `;
  }

  /** The single "hour of day" control that owns hour_sin / hour_cos. */
  private renderHourControl(): TemplateResult {
    const h = spHour.value;
    return html`
      <div class="sp-feature sp-feature-hour">
        <div class="sp-feature-head">
          <span class="sp-feature-name" title="Local (nautical) hour of day"
            >Hour of day</span
          >
          <span class="sp-feature-val">${String(h).padStart(2, '0')}:00</span>
        </div>
        <input
          class="sp-slider"
          type="range"
          min="0"
          max="23"
          step="1"
          .value=${String(h)}
          @input=${this.onHour}
        />
        <div class="sp-range-labels">
          <span>00:00</span>
          <span>23:00</span>
        </div>
      </div>
    `;
  }

  /** Read-only chips for the locked derived features. */
  private renderDerivedChips(): TemplateResult {
    const derived = (spSchema.value?.features ?? []).filter((f) =>
      DERIVED_SET.has(f.name),
    );
    if (!derived.length) return html``;
    return html`
      <div class="sp-derived">
        <div class="sp-derived-head">
          <span class="sp-lock" aria-hidden="true">🔒</span>
          <span>Derived - computed automatically</span>
        </div>
        <div class="sp-derived-chips">
          ${derived.map((f) => {
            const missing = f.kind === 'missing';
            const unit = unitOf(f.name);
            return html`<div class="sp-chip ${missing ? 'sp-chip-missing' : ''}" title=${f.name}>
              <span class="sp-chip-name">${labelOf(f.name)}</span>
              <span class="sp-chip-val"
                >${missing ? 'n/a' : fmt(spValues.value[f.name])}${!missing && unit
                  ? html` <small>${unit}</small>`
                  : nothing}</span
              >
            </div>`;
          })}
        </div>
      </div>
    `;
  }

  // ── SHAP contributions ───────────────────────────────────
  // HIDDEN FOR NOW: the right panel shows the dependence plot (renderDependencePlot)
  // instead of the SHAP tornado. The machinery below (renderShapTitle /
  // renderContributions, runExplain, the /explain endpoint + spExplain atom) is
  // kept intact so re-enabling is a one-line swap in render(). runExplain is no
  // longer called from the input handlers while SHAP is hidden.

  /** SHAP tornado: per-feature additive contributions (sorted by magnitude),
   * with hour_sin/hour_cos merged into one "hour of day" row, followed by the
   * SP correction term as a visually distinct block. */
  private renderShapTitle(): TemplateResult {
    return html`<div class="sp-panel-title">
      Contributions (SHAP) value
      ${this.renderInfo(
        'shap',
        this.shapInfoOpen,
        () => (this.shapInfoOpen = !this.shapInfoOpen),
        html`Each value is how many kW that input pushes <i>this</i> prediction above (blue)
          or below (red) the model's average output - the <b>base (mean)</b> shown on top.
          Base + all contributions + the SP correction = the final corrected power.
          <br /><br />
          They explain this single prediction, <b>not</b> overall feature importance, and
          they're correlations the model learned - <b>not</b> proof of cause and effect.`,
        'right',
      )}
    </div>`;
  }

  private renderContributions(): TemplateResult {
    const ex = spExplain.value;
    if (this.hasMissing) {
      return html`<div class="sp-panel sp-shap">
        ${this.renderShapTitle()}
        <p class="sp-hint">Unavailable - some model features aren't in this ship's data.</p>
      </div>`;
    }
    if (!ex) {
      return html`<div class="sp-panel sp-shap">
        ${this.renderShapTitle()}
        <p class="sp-hint"><span class="spinner"></span> Computing…</p>
      </div>`;
    }

    // Merge the cyclical hour pair into a single contribution (their sum is the
    // hour control's total attribution); leave every other feature as-is.
    let hourSum = 0;
    let hasHour = false;
    const merged: SPContribution[] = [];
    for (const c of ex.contributions) {
      if (HOUR_PAIR.has(c.name)) {
        hourSum += c.value;
        hasHour = true;
      } else {
        merged.push(c);
      }
    }
    if (hasHour) merged.push({ name: HOUR_LABEL, value: hourSum });

    const rows = merged.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    // A null correction means none is on record for this ship/moment - show it as
    // missing rather than as "+0 kW" (which reads as broken). For the waterfall
    // math a missing correction is simply no adjustment.
    const hasCorrection = spCorrection.value != null;
    const correction = spCorrection.value ?? 0;
    const corrTs = spCorrectionTimestamp.value;
    const maxAbs = Math.max(...rows.map((r) => Math.abs(r.value)), Math.abs(correction), 1e-9);
    const corrected = ex.predicted + correction;
    const actual = spActual.value;
    const delta = actual != null ? corrected - actual : null;
    return html`
      <div class="sp-panel sp-shap">
        ${this.renderShapTitle()}
        <div class="sp-waterfall-row sp-waterfall-base" title="Model average output before any input">
          <span class="sp-waterfall-name">Base (mean)</span>
          <span class="sp-waterfall-num">${fmtKw(ex.base_value)} kW</span>
        </div>
        <div class="sp-shap-rows">
          ${rows.map((r) => {
            const pct = (Math.abs(r.value) / maxAbs) * 50; // half-width = 50%
            const pos = r.value >= 0;
            return html`<div class="sp-shap-row" title="${labelOf(r.name)}: ${fmtKw(r.value, true)} kW">
              <span class="sp-shap-name">${labelOf(r.name)}</span>
              <span class="sp-shap-track">
                <span
                  class="sp-shap-bar ${pos ? 'sp-pos' : 'sp-neg'}"
                  style=${pos ? `left:50%;width:${pct}%` : `right:50%;width:${pct}%`}
                ></span>
              </span>
              <span class="sp-shap-num ${pos ? 'sp-pos' : 'sp-neg'}"
                >${fmtKw(r.value, true)} kW</span
              >
            </div>`;
          })}
        </div>
        <div class="sp-correction-block ${hasCorrection ? '' : 'sp-correction-absent'}">
          <div class="sp-correction-row" title="Additive SP correction factor from DynamoDB">
            <span class="sp-correction-name">SP correction</span>
            <span class="sp-correction-num"
              >${hasCorrection ? html`${fmtKw(correction, true)} kW` : 'missing'}</span
            >
          </div>
          <div class="sp-correction-sub">
            ${corrTs
              ? html`factor recorded ${fmtTsFull(corrTs)} UTC`
              : 'no correction factor recorded before this moment'}
          </div>
        </div>
        <div class="sp-waterfall-row sp-waterfall-final" title="Base + contributions + SP correction">
          <span class="sp-waterfall-name">Final (corrected)</span>
          <span class="sp-waterfall-num">${fmtKw(corrected)} kW</span>
        </div>
        <div class="sp-waterfall-row sp-waterfall-actual" title="Actual recorded service power at the loaded moment">
          <span class="sp-waterfall-name">Actual</span>
          <span class="sp-waterfall-num">${actual == null ? '-' : `${fmtKw(actual)} kW`}</span>
        </div>
        <div class="sp-waterfall-row sp-waterfall-delta" title="Corrected prediction − actual">
          <span class="sp-waterfall-name">Δ corrected − actual</span>
          <span class="sp-waterfall-num">${delta == null ? '-' : `${fmtKw(delta, true)} kW`}</span>
        </div>
      </div>
    `;
  }

  // ── Dependence plot (SP over one feature) ────────────────

  /** The right-panel dependence plot: predicted SP swept over the selected
   * x-feature (every other input held at its slider value), with the real data
   * overlaid as a conditioned scatter. `baseFeatures` supplies the selector. */
  private renderDependencePlot(baseFeatures: SPFeature[]): TemplateResult {
    const directFeatures = baseFeatures.filter((f) => f.kind === 'direct');
    const xFeature = spXFeature.value;
    // Sweepable axes: hour-of-day first (when the model has it), then the direct
    // features. Each is an {value, label} pair so "hour" can carry a friendly label.
    const options: Array<{ value: string; label: string }> = [
      ...(this.modelHasHour ? [{ value: HOUR_X, label: HOUR_LABEL }] : []),
      ...directFeatures.map((f) => ({ value: f.name, label: labelOf(f.name) })),
    ];
    const header = html`
      <div class="sp-panel-title sp-dep-title">
        <span class="sp-dep-vs">Service Power vs</span>
        <select
          class="sp-dep-select"
          .value=${live(xFeature ?? '')}
          ?disabled=${this.hasMissing || options.length === 0}
          @change=${this.onXFeature}
        >
          ${options.map(
            // `live()` forces the select's DOM value to match the chosen feature on
            // every render - a plain `.value` binding would stick to the first
            // option, since the live selection is set before/independent of the
            // option `selected` attributes across re-renders.
            (o) => html`<option value=${o.value}>${o.label}</option>`,
          )}
        </select>
        <span class="sp-legend">
          <span class="sp-legend-item"><i class="sp-sw-pred"></i>model</span>
          ${this.renderActualLegendItem()}
        </span>
      </div>
      <div class="sp-panel-sub sp-dep-note">
        line = model (others held at the sliders) · dots = actual service power
        (excl. ALS/AAQS) where other inputs are within ±${Math.round(TOL_FRAC * 100)}% of
        their set values
      </div>
    `;

    let body: TemplateResult;
    if (this.hasMissing) {
      body = html`<p class="sp-hint">Unavailable - some model features aren't in this ship's data.</p>`;
    } else if (!xFeature) {
      body = html`<p class="sp-hint">No feature available to plot.</p>`;
    } else if (spSweepLoading.value && !spSweep.value) {
      body = html`<p class="sp-hint"><span class="spinner"></span> Sweeping the model…</p>`;
    } else {
      body = this.renderDependenceChart(xFeature);
    }
    return html`<div class="sp-panel sp-dep">${header}${body}</div>`;
  }

  private renderDependenceChart(xFeature: string): TemplateResult {
    const sweep = spSweep.value;
    if (!sweep || sweep.x.length === 0) {
      return html`<div class="sp-hist-empty">no model curve for the selected range</div>`;
    }
    const S = 1000;
    const n = sweep.x.length;
    const isHour = xFeature === HOUR_X;
    const xLabel = isHour ? HOUR_LABEL : labelOf(xFeature);
    const xUnit = isHour ? '' : unitOf(xFeature);
    const fmtX = (v: number | null | undefined): string =>
      v == null || !Number.isFinite(v)
        ? '-'
        : isHour
          ? `${String(Math.round(v)).padStart(2, '0')}:00`
          : `${fmt(v)}${xUnit ? ` ${xUnit}` : ''}`;
    const correction = spCorrection.value ?? 0;
    // The line is reported CORRECTED (model + the per-moment SP correction), to
    // match the scatter of actual service power. The correction is constant
    // across x - it depends only on the loaded moment, like the timeline.
    const lineY = sweep.predicted.map((v) => v + correction);

    // Y domain spans both the line and the scatter so neither clips; rounded to
    // nice ticks (matching the timeline's y-axis).
    const finiteY: number[] = [];
    for (const v of lineY) if (Number.isFinite(v)) finiteY.push(v);
    for (const v of sweep.scatter_y) if (Number.isFinite(v)) finiteY.push(v);
    if (!finiteY.length) {
      return html`<div class="sp-hist-empty">no finite values to plot</div>`;
    }
    let yLo = Math.min(...finiteY);
    let yHi = Math.max(...finiteY);
    if (yLo === yHi) yHi = yLo + 1;
    const yScale = niceTicks(yLo, yHi, 5);

    // X domain is EXACTLY the swept feature's slider range (= the histogram caps),
    // not rounded outward - so the axis ends where the sliders/histograms do (e.g.
    // STW stops at its real max, not an inflated 30 kn). Tick *values* are still
    // rounded for readability, then clipped to the domain.
    const xLo = sweep.x[0]!;
    const xHi = sweep.x[n - 1]!;
    const xSpan = xHi - xLo || 1;
    const ySpan = yScale.max - yScale.min || 1;
    const fx = (v: number) => (v - xLo) / xSpan; // 0..1
    const fy = (v: number) => 1 - (v - yScale.min) / ySpan; // 0..1

    let linePath = '';
    let pen = false;
    sweep.x.forEach((xv, i) => {
      const yv = lineY[i]!;
      if (!Number.isFinite(yv)) {
        pen = false;
        return;
      }
      linePath += `${pen ? 'L' : 'M'}${(fx(xv) * S).toFixed(1)} ${(fy(yv) * S).toFixed(1)} `;
      pen = true;
    });

    const yTicks = yScale.ticks.map((v) => ({ v, topPct: fy(v) * 100 }));
    const xTicks = niceTicks(xLo, xHi, 6)
      .ticks.filter((v) => v >= xLo - 1e-9 && v <= xHi + 1e-9)
      .map((v) => ({ v, leftPct: fx(v) * 100 }));

    // Snapshot marker: where the loaded moment sits (its x value vs its actual SP),
    // so "Load values" anchors the curve to a real point. For an hour sweep the x
    // is the loaded hour-of-day (spHour), not a model-feature value.
    const snapX = isHour ? spHour.value : spValues.value[xFeature];
    const snapY = spActual.value;
    const hasSnap =
      snapX != null &&
      Number.isFinite(snapX) &&
      snapY != null &&
      Number.isFinite(snapY) &&
      snapX >= xLo &&
      snapX <= xHi;

    const hov = this.sweepHoverIdx;
    const cursorX = hov != null && hov >= 0 && hov < n ? fx(sweep.x[hov]!) * S : null;

    return html`
      <div class="sp-dep-readout">
        ${hov != null && hov >= 0 && hov < n
          ? html`<span>${xLabel} ${fmtX(sweep.x[hov])}</span>
              <span class="sp-sw-pred-text">model ${fmtKw(lineY[hov])} kW</span>`
          : html`<span class="sp-dep-count">${sweep.scatter_count} actual points</span>`}
      </div>
      <div class="sp-ts-frame">
        <div class="sp-ts-row sp-ts-row-main">
          <div class="sp-ts-yaxis">
            ${yTicks.map(
              (t) =>
                html`<span class="sp-ts-ytick" style="top:${t.topPct.toFixed(1)}%"
                  >${fmtKw(t.v)}</span
                >`,
            )}
          </div>
          <div class="sp-ts-plot">
            <svg
              class="sp-ts-svg"
              viewBox="0 0 ${S} ${S}"
              preserveAspectRatio="none"
              @mousemove=${(e: MouseEvent) => this.onSweepMove(e, n, xLo, xSpan, xLo, xHi)}
              @mouseleave=${() => (this.sweepHoverIdx = null)}
            >
              ${yTicks.map(
                (t) =>
                  svg`<line class="sp-ts-grid-line" x1="0" x2=${S} y1=${(fy(t.v) * S).toFixed(1)} y2=${(fy(t.v) * S).toFixed(1)} />`,
              )}
              ${sweep.scatter_x.map((sx, i) => {
                const sy = sweep.scatter_y[i]!;
                if (!Number.isFinite(sx) || !Number.isFinite(sy)) return nothing;
                return svg`<circle class="sp-dep-dot" cx=${(fx(sx) * S).toFixed(1)} cy=${(fy(sy) * S).toFixed(1)} r="4" />`;
              })}
              ${svg`<path class="sp-dep-line" d=${linePath.trim()} />`}
              ${hasSnap
                ? svg`<circle class="sp-dep-snap" cx=${(fx(snapX) * S).toFixed(1)} cy=${(fy(snapY) * S).toFixed(1)} r="7" />`
                : nothing}
              ${cursorX == null
                ? nothing
                : svg`<line class="sp-ts-cursor" x1=${cursorX} x2=${cursorX} y1="0" y2=${S} />`}
            </svg>
          </div>
        </div>
        <div class="sp-ts-row sp-ts-row-x">
          <div class="sp-ts-xspacer"></div>
          <div class="sp-ts-xaxis">
            ${xTicks.map((t) => {
              const anchor =
                t.leftPct <= 1 ? 'left:0' : t.leftPct >= 99 ? 'right:0' : `left:${t.leftPct.toFixed(1)}%`;
              const cls =
                t.leftPct <= 1
                  ? 'sp-ts-xtick sp-x-start'
                  : t.leftPct >= 99
                    ? 'sp-ts-xtick sp-x-end'
                    : 'sp-ts-xtick';
              return html`<span class=${cls} style=${anchor}>${fmtX(t.v)}</span>`;
            })}
          </div>
        </div>
      </div>
    `;
  }

  private onSweepMove(
    e: MouseEvent,
    n: number,
    xMin: number,
    xSpan: number,
    x0: number,
    xLast: number,
  ): void {
    const rect = (e.currentTarget as Element).getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / (rect.width || 1)));
    // The plot's x runs over the nice axis [xMin, xMin+xSpan]; the sweep grid is a
    // uniform linspace over [x0, xLast] within it. Convert the cursor fraction to
    // a feature value, then to the nearest grid index, so the readout/cursor track
    // the actual sampled point under the pointer (not the axis edges).
    const xVal = xMin + frac * xSpan;
    const span = xLast - x0 || 1;
    this.sweepHoverIdx = Math.min(n - 1, Math.max(0, Math.round(((xVal - x0) / span) * (n - 1))));
  }

  private renderControls(ship: string, versions: string[]): TemplateResult {
    return html`
      <div class="sp-controls">
        <label class="sp-ctl">
          <span>Model · ${ship}</span>
          <select
            .value=${spVersion.value ?? ''}
            ?disabled=${!versions.length}
            @change=${(e: Event) => spVersion.set((e.target as HTMLSelectElement).value)}
          >
            ${versions.map((v) => html`<option value=${v}>${v}</option>`)}
          </select>
        </label>
        <label class="sp-ctl">
          <span>
            Init from datetime (UTC)
            ${this.renderInfo(
              'date',
              this.dateInfoOpen,
              () => (this.dateInfoOpen = !this.dateInfoOpen),
              html`Pick a past moment and press <b>Load values</b> to fill the sliders with
                what this ship actually recorded then - the SP correction factor in force at
                that time is applied too. <b>Load latest</b> jumps to the most recent record.`,
            )}
          </span>
          <input
            type="datetime-local"
            step="1"
            .value=${spInitDatetime.value || modelEnd.value}
            @change=${this.onInitDatetime}
          />
        </label>
        <button
          class="btn btn-primary sp-load-btn"
          ?disabled=${!spVersion.value || this.initing}
          title="Load the feature values recorded at the selected datetime"
          @click=${() => this.loadValues()}
        >
          ${this.initing ? 'Loading…' : 'Load values'}
        </button>
        <button
          class="btn btn-secondary sp-load-btn"
          ?disabled=${!spVersion.value || this.initing}
          title="Load the most recent available record and its correction factor"
          @click=${() => this.loadLatest()}
        >
          Load latest
        </button>
      </div>
    `;
  }

  /** A small ⓘ info affordance: hover or click toggles a short explainer popover.
   * `open` (a pinned-by-click flag) keeps it visible after a click; hover shows
   * it transiently via CSS. */
  private renderInfo(
    key: string,
    open: boolean,
    toggle: () => void,
    body: TemplateResult,
    align: 'left' | 'right' = 'left',
  ): TemplateResult {
    // `align` anchors the popover's edge to the icon: 'right' opens leftward,
    // which keeps it on-screen for icons near the right side (the SHAP title).
    return html`<span
      class="sp-info ${open ? 'sp-info-open' : ''} ${align === 'right' ? 'sp-info-right' : ''}"
      data-info=${key}
    >
      <button
        type="button"
        class="sp-info-btn"
        aria-label="More information"
        @click=${(e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
      >
        ⓘ
      </button>
      <span class="sp-info-pop" role="tooltip">${body}</span>
    </span>`;
  }

  /** The "actual" legend item, shared by the dependence plot and the timeline.
   * Makes the basis explicit: the model predicts service power EXCLUDING the
   * air-lubrication (ALS) / air-quality (AAQS) system loads, so the overlaid
   * actual subtracts them too (ships without those systems count them as 0 -
   * see TARGET_NAME / _add_derived in backend/sp_model.py). */
  private renderActualLegendItem(): TemplateResult {
    return html`<span class="sp-legend-item">
      <i class="sp-sw-actual"></i>actual<span class="sp-legend-excl">excl. ALS/AAQS</span>
      ${this.renderInfo(
        'actual',
        this.actualInfoOpen,
        () => (this.actualInfoOpen = !this.actualInfoOpen),
        html`<b>Actual</b> is the ship's recorded service power minus its
          air-lubrication (<b>ALS</b>) and advanced-air-quality (<b>AAQS</b>) system
          loads - the exact quantity this model is trained to predict, so the line
          and the dots are on the same basis. Ships without those systems show raw
          service power (ALS/AAQS count as 0).`,
        'right',
      )}
    </span>`;
  }

  private renderTimeline(): TemplateResult {
    const ts = spTimeseries.value;
    const loading = spTimeseriesLoading.value;
    return html`
      <div class="sp-panel sp-timeline">
        <div class="sp-panel-title">
          Timeline
          <span class="sp-panel-sub"
            >predicted vs actual service power, excl. ALS/AAQS (15-min avg) - click to
            load a moment</span
          >
          <span class="sp-legend">
            <span class="sp-legend-item"><i class="sp-sw-pred"></i>predicted</span>
            ${this.renderActualLegendItem()}
          </span>
        </div>
        ${loading
          ? html`<p class="sp-hint"><span class="spinner"></span> Running the model over the window…</p>`
          : this.renderTimelineChart(ts)}
      </div>
    `;
  }

  private renderTimelineChart(
    ts: { timestamps: string[]; corrected: Array<number | null>; actual: Array<number | null> } | null,
  ): TemplateResult {
    if (!ts || ts.timestamps.length === 0) {
      return html`<div class="sp-hist-empty">no data in the selected window</div>`;
    }
    // Normalised drawing space (0..1000 on both axes); preserveAspectRatio="none"
    // stretches it to fill the plot cell. Axis ticks live in HTML gutters around
    // the SVG (so their text isn't distorted by the stretch).
    const S = 1000;
    const n = ts.timestamps.length;
    const finite: number[] = [];
    for (const v of ts.corrected) if (v != null && Number.isFinite(v)) finite.push(v);
    for (const v of ts.actual) if (v != null && Number.isFinite(v)) finite.push(v);
    if (!finite.length) {
      return html`<div class="sp-hist-empty">no finite values in the window</div>`;
    }
    let dataLo = Math.min(...finite);
    let dataHi = Math.max(...finite);
    if (dataLo === dataHi) dataHi = dataLo + 1;
    // Round the axis out to a 1/2/5×10ⁿ step so ticks land on readable numbers
    // (…, 5000, 10000, …) rather than arbitrary data-derived values.
    const scale = niceTicks(dataLo, dataHi, 5);
    const lo = scale.min;
    const hi = scale.max;
    const fx = (i: number) => (n === 1 ? 0.5 : i / (n - 1)); // 0..1
    const fy = (v: number) => 1 - (v - lo) / (hi - lo); // 0..1
    const path = (vals: Array<number | null>): string => {
      let d = '';
      let pen = false;
      vals.forEach((v, i) => {
        if (v == null || !Number.isFinite(v)) {
          pen = false;
          return;
        }
        d += `${pen ? 'L' : 'M'}${(fx(i) * S).toFixed(1)} ${(fy(v) * S).toFixed(1)} `;
        pen = true;
      });
      return d.trim();
    };

    // Axis ticks: the rounded tick values from the nice scale.
    const yTicks = scale.ticks.map((v) => ({ v, topPct: fy(v) * 100 }));
    const X_TICKS = Math.min(5, n);
    const xTicks = Array.from({ length: X_TICKS }, (_, k) => {
      const i = X_TICKS === 1 ? 0 : Math.round((k / (X_TICKS - 1)) * (n - 1));
      return { i, leftPct: fx(i) * 100, label: fmtTsFull(ts.timestamps[i]) };
    });

    const hi_ = this.hoverIdx;
    const cursorX = hi_ != null ? fx(hi_) * S : null;
    const hv = (arr: Array<number | null>) => (hi_ != null ? arr[hi_] : null);
    const hoverCorr = hi_ != null ? this.correctionInForce(ts.timestamps[hi_]) : null;

    // Map real times onto the index-based x-axis (shared by the correction strip
    // and the selected-datetime marker).
    const tsEpochs = ts.timestamps.map((t) => Date.parse(t));
    const timeToFrac = (epoch: number): number => SpModelViewer.fracForEpoch(tsEpochs, epoch);
    const corrStrip = this.buildCorrectionStrip(timeToFrac, S);

    // Persistent marker at the loaded/selected datetime (picker or click). The
    // picker value is a naive UTC string ("…THH:MM:SS"); parse it as UTC.
    const selIso = spInitDatetime.value;
    let markerX: number | null = null;
    if (selIso) {
      const e = Date.parse(/[Zz]|[+-]\d\d:?\d\d$/.test(selIso) ? selIso : `${selIso}Z`);
      if (Number.isFinite(e) && e >= tsEpochs[0]! && e <= tsEpochs[n - 1]!) {
        markerX = timeToFrac(e) * S;
      }
    }

    return html`
      <div class="sp-timeline-readout">
        ${hi_ != null
          ? html`<span>${fmtTs(ts.timestamps[hi_])}</span>
              <span class="sp-sw-pred-text">predicted ${fmtKw(hv(ts.corrected))} kW</span>
              <span class="sp-sw-actual-text">actual ${fmtKw(hv(ts.actual))} kW</span>
              ${hoverCorr == null
                ? nothing
                : html`<span class="sp-sw-corr-text">correction ${fmtKw(hoverCorr, true)} kW</span>`}`
          : nothing}
      </div>
      <div class="sp-ts-frame">
        <div class="sp-ts-row sp-ts-row-main">
          <div class="sp-ts-yaxis">
            ${yTicks.map(
              (t) =>
                html`<span class="sp-ts-ytick" style="top:${t.topPct.toFixed(1)}%"
                  >${fmtKw(t.v)}</span
                >`,
            )}
          </div>
          <div class="sp-ts-plot">
            <svg
              class="sp-ts-svg"
              viewBox="0 0 ${S} ${S}"
              preserveAspectRatio="none"
              @mousemove=${(e: MouseEvent) => this.onTimelineMove(e, n)}
              @mouseleave=${() => (this.hoverIdx = null)}
              @click=${() => this.onTimelineClick(ts.timestamps)}
            >
              ${yTicks.map(
                (t) =>
                  svg`<line class="sp-ts-grid-line" x1="0" x2=${S} y1=${(fy(t.v) * S).toFixed(1)} y2=${(fy(t.v) * S).toFixed(1)} />`,
              )}
              ${svg`<path class="sp-ts-actual" d=${path(ts.actual)} />`}
              ${svg`<path class="sp-ts-pred" d=${path(ts.corrected)} />`}
              ${markerX == null
                ? nothing
                : svg`<line class="sp-ts-marker" x1=${markerX} x2=${markerX} y1="0" y2=${S} />`}
              ${cursorX == null
                ? nothing
                : svg`<line class="sp-ts-cursor" x1=${cursorX} x2=${cursorX} y1="0" y2=${S} />`}
            </svg>
          </div>
        </div>
        ${corrStrip
          ? html`
              <div class="sp-ts-row sp-ts-row-corr">
                <div class="sp-ts-yaxis">
                  <span class="sp-ts-ytick" style="top:16%">${fmtKw(corrStrip.hi, true)}</span>
                  <span class="sp-ts-ytick" style="top:84%">${fmtKw(corrStrip.lo, true)}</span>
                </div>
                <div class="sp-ts-plot sp-corr-plot">
                  <span class="sp-corr-label">SP correction (kW)</span>
                  <svg class="sp-ts-svg sp-corr-svg" viewBox="0 0 ${S} ${S}" preserveAspectRatio="none">
                    ${svg`<line class="sp-corr-zero" x1="0" x2=${S} y1=${(corrStrip.zeroY * S).toFixed(1)} y2=${(corrStrip.zeroY * S).toFixed(1)} />`}
                    ${svg`<path class="sp-corr-step" d=${corrStrip.d} />`}
                    ${markerX == null
                      ? nothing
                      : svg`<line class="sp-ts-marker" x1=${markerX} x2=${markerX} y1="0" y2=${S} />`}
                    ${cursorX == null
                      ? nothing
                      : svg`<line class="sp-ts-cursor" x1=${cursorX} x2=${cursorX} y1="0" y2=${S} />`}
                  </svg>
                </div>
              </div>
            `
          : nothing}
        <div class="sp-ts-row sp-ts-row-x">
          <div class="sp-ts-xspacer"></div>
          <div class="sp-ts-xaxis">
            ${xTicks.map((t) => {
              const anchor = t.leftPct <= 1 ? 'left:0' : t.leftPct >= 99 ? 'right:0' : `left:${t.leftPct.toFixed(1)}%`;
              const cls = t.leftPct <= 1 ? 'sp-ts-xtick sp-x-start' : t.leftPct >= 99 ? 'sp-ts-xtick sp-x-end' : 'sp-ts-xtick';
              return html`<span class=${cls} style=${anchor}>${t.label}</span>`;
            })}
          </div>
        </div>
      </div>
    `;
  }

  /** Fractional x-position (0..1) of an epoch within the sorted `epochs` array,
   * linearly interpolated in time between the surrounding samples and clamped to
   * the ends - the timeline's x-axis is index-based, so this maps real times onto
   * it. Shared by the correction strip and the selected-datetime marker. */
  private static fracForEpoch(epochs: number[], epoch: number): number {
    const n = epochs.length;
    if (!Number.isFinite(epoch) || n <= 1) return 0;
    const t0 = epochs[0]!;
    const tN = epochs[n - 1]!;
    if (tN <= t0 || epoch <= t0) return 0;
    if (epoch >= tN) return 1;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (epochs[mid]! <= epoch) lo = mid;
      else hi = mid;
    }
    const span = epochs[hi]! - epochs[lo]! || 1;
    return (lo + (epoch - epochs[lo]!) / span) / (n - 1);
  }

  /** The SP correction factor (kW) in force at `iso` - the most recent step at or
   * before it (0 before the first one), or null when corrections are unavailable. */
  private correctionInForce(iso: string | undefined): number | null {
    const corr = spCorrectionsSeries.value?.corrections;
    if (!corr || !iso) return null;
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return null;
    let val = 0; // nothing in force yet → 0, matching the corrected-series math
    for (const c of corr) {
      if (Date.parse(c.timestamp) <= t) val = c.value;
      else break; // corrections are sorted ascending
    }
    return val;
  }

  /** Build the correction step-path over the timeline window, mapping each
   * correction's write time onto the main chart's index-fraction x-axis (via
   * `timeToFrac`) so the steps line up with the corrected curve. Returns null when
   * there's nothing to draw. `lo`/`hi` are the y-domain (kW), `zeroY` the 0-kW
   * gridline in 0..1. */
  private buildCorrectionStrip(
    timeToFrac: (epoch: number) => number,
    S: number,
  ): { d: string; lo: number; hi: number; zeroY: number } | null {
    const corr: SPCorrectionPoint[] = spCorrectionsSeries.value?.corrections ?? [];
    if (!corr.length) return null;

    const vals = corr.map((c) => c.value);
    const fracs = corr.map((c) => timeToFrac(Date.parse(c.timestamp)));
    // Pad the y-domain to always include 0 (so the zero line is meaningful) and
    // give a flat (single-value) series some vertical breathing room.
    let lo = Math.min(0, ...vals);
    let hi = Math.max(0, ...vals);
    if (lo === hi) {
      lo -= 1;
      hi += 1;
    }
    const yc = (v: number): number => 1 - (v - lo) / (hi - lo);
    let d = '';
    corr.forEach((_, i) => {
      const xStart = i === 0 ? 0 : fracs[i]! * S;
      const xEnd = (i === corr.length - 1 ? 1 : fracs[i + 1]!) * S;
      const y = (yc(vals[i]!) * S).toFixed(1);
      // i>0 begins with a vertical riser (L into xStart at the new value).
      d += `${i === 0 ? 'M' : 'L'}${xStart.toFixed(1)} ${y} L${xEnd.toFixed(1)} ${y} `;
    });
    return { d: d.trim(), lo, hi, zeroY: yc(0) };
  }

  private onTimelineMove(e: MouseEvent, n: number): void {
    // Measure against the SVG's box via clientX, NOT e.offsetX: offsetX is
    // relative to e.target (whichever child line/path is under the cursor), so a
    // click landing on a drawn curve would resolve to the wrong index - biased
    // left, since the children start right of the SVG's left edge.
    const rect = (e.currentTarget as Element).getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / (rect.width || 1)));
    this.hoverIdx = Math.round(frac * (n - 1));
  }

  private onTimelineClick(timestamps: string[]): void {
    if (this.hoverIdx == null) return;
    const iso = timestamps[this.hoverIdx];
    if (!iso) return;
    // The init endpoint wants a naive (UTC) datetime-local string.
    const dt = new Date(iso).toISOString().slice(0, 19);
    spInitDatetime.set(dt);
    void this.loadAt(dt);
  }

  override render(): TemplateResult {
    const ship = modelShips.value[0] ?? null;
    const schema = spSchema.value;
    const versions = spVersions.value;
    const err = spError.value;

    if (!ship) {
      return html`<div class="card sp-card">
        <p class="sp-hint">Select a ship to explore its service-power model.</p>
      </div>`;
    }

    const baseFeatures = (schema?.features ?? []).filter((f) => !DERIVED_SET.has(f.name));
    const hasHour = (schema?.features ?? []).some(
      (f) => (f.name === 'hour_sin' || f.name === 'hour_cos') && f.kind === 'derived',
    );

    return html`
      <div class="card sp-card">
        ${schema ? this.renderControls(ship, versions) : nothing}
        ${err ? html`<div class="sp-error">${err}</div>` : nothing}
        ${spLoading.value && !schema
          ? html`<p class="sp-hint"><span class="spinner"></span> Loading model…</p>`
          : nothing}
        ${schema
          ? html`
              <div class="sp-explorer">
                <div class="sp-panel sp-whatif">
                  <div class="sp-panel-title">
                    Input features
                    <span class="sp-panel-sub">drag to perturb · derived stay locked</span>
                  </div>
                  <div class="sp-feature-grid">
                    ${baseFeatures
                      // The swept feature is the plot's x-axis, not a held input -
                      // drop it from the slider column while it's selected.
                      .filter((f) => f.name !== spXFeature.value)
                      .map((f) => this.renderFeature(f))}
                    ${hasHour && spXFeature.value !== HOUR_X
                      ? this.renderHourControl()
                      : nothing}
                  </div>
                  ${this.renderDerivedChips()}
                </div>
                <div class="sp-explorer-side">
                  ${this.renderDependencePlot(baseFeatures)}
                </div>
              </div>
              ${this.renderTimeline()}
            `
          : nothing}
      </div>
    `;
  }
}

/** One histogram bar. Uses Lit's `svg` tag so the element is created in the SVG
 * namespace - an `html` fragment here would render as a 0×0 unknown element. */
function svgRect(x: number, y: number, w: number, h: number): SVGTemplateResult {
  return svg`<rect x=${x} y=${y} width=${w} height=${h} />`;
}

declare global {
  interface HTMLElementTagNameMap {
    'spmodel-viewer': SpModelViewer;
  }
}
